import { type StateCreator } from 'zustand';

import { type WorkspaceAgentOnboardingAnswers } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentOnboardingHandoff';

/** Which thread the scripted example belongs to. Lives on the panel store so a chat remount keeps it. */
export type WorkspaceAgentOnboardingHandoffSession = {
  boundSessionId: string | null;
  openingSessionId: string | null;
};

/** Ephemeral onboarding intro coordination. Not persisted — reload or a real session drops it. */
export type WorkspaceAgentPanelOnboardingSlice = {
  /**
   * Lifts the portalled agent panel above the onboarding sky overlay (`z-portal` is 100000). Not
   * persisted — the intro clears it when the sky finishes lifting.
   */
  isElevatedAboveOnboarding: boolean;
  setIsElevatedAboveOnboarding: (isElevatedAboveOnboarding: boolean) => void;
  /**
   * The intro's answers while that first run is on screen. Not persisted and not left on the URL —
   * a reload or a real session drops it.
   */
  onboardingHandoff: WorkspaceAgentOnboardingAnswers | null;
  setOnboardingHandoff: (onboardingHandoff: WorkspaceAgentOnboardingAnswers | null) => void;
  /** True once the simulated reply is on screen. The sky waits on this, not on its own clock. */
  isOnboardingReplyReady: boolean;
  setIsOnboardingReplyReady: (isOnboardingReplyReady: boolean) => void;
  /** True once the sky has finished leaving. The try-it block waits on this. */
  isOnboardingSkyDismissed: boolean;
  setIsOnboardingSkyDismissed: (isOnboardingSkyDismissed: boolean) => void;
  /**
   * True once the scripted example has been read and the live job is about to start. The mock
   * stays on screen; this line is what separates it from the real send.
   */
  isOnboardingHybridTurnLineVisible: boolean;
  setIsOnboardingHybridTurnLineVisible: (isOnboardingHybridTurnLineVisible: boolean) => void;
  /** The thread the scripted example is bound to. Null until that first run is on screen. */
  onboardingHandoffSession: WorkspaceAgentOnboardingHandoffSession | null;
  setOnboardingHandoffSession: (onboardingHandoffSession: WorkspaceAgentOnboardingHandoffSession | null) => void;
  /** Drops the in-progress intro so a replay starts from the first screen. */
  resetOnboarding: () => void;
};

type CreateWorkspaceAgentPanelOnboardingSlice = () => StateCreator<
  WorkspaceAgentPanelOnboardingSlice,
  [],
  [],
  WorkspaceAgentPanelOnboardingSlice
>;

export const createWorkspaceAgentPanelOnboardingSlice: CreateWorkspaceAgentPanelOnboardingSlice = () => (set, get) => ({
  isElevatedAboveOnboarding: false,
  onboardingHandoff: null,
  isOnboardingReplyReady: false,
  isOnboardingSkyDismissed: false,
  isOnboardingHybridTurnLineVisible: false,
  onboardingHandoffSession: null,

  setIsElevatedAboveOnboarding: (isElevatedAboveOnboarding) => {
    if (isElevatedAboveOnboarding === get().isElevatedAboveOnboarding) {
      return;
    }

    set({ isElevatedAboveOnboarding });
  },

  setOnboardingHandoff: (onboardingHandoff) => {
    if (onboardingHandoff === get().onboardingHandoff) {
      return;
    }

    set({
      onboardingHandoff,
      onboardingHandoffSession: null,
      // A cleared handoff is a new thread. The turn line belongs to the run that just ended.
      ...(onboardingHandoff === null ? { isOnboardingHybridTurnLineVisible: false } : {}),
    });
  },

  setIsOnboardingReplyReady: (isOnboardingReplyReady) => {
    if (isOnboardingReplyReady === get().isOnboardingReplyReady) {
      return;
    }

    set({ isOnboardingReplyReady });
  },

  setIsOnboardingSkyDismissed: (isOnboardingSkyDismissed) => {
    if (isOnboardingSkyDismissed === get().isOnboardingSkyDismissed) {
      return;
    }

    set({ isOnboardingSkyDismissed });
  },

  setIsOnboardingHybridTurnLineVisible: (isOnboardingHybridTurnLineVisible) => {
    if (isOnboardingHybridTurnLineVisible === get().isOnboardingHybridTurnLineVisible) {
      return;
    }

    set({ isOnboardingHybridTurnLineVisible });
  },

  setOnboardingHandoffSession: (onboardingHandoffSession) => {
    set({ onboardingHandoffSession });
  },

  resetOnboarding: () => {
    set({
      isElevatedAboveOnboarding: false,
      onboardingHandoff: null,
      isOnboardingReplyReady: false,
      isOnboardingSkyDismissed: false,
      isOnboardingHybridTurnLineVisible: false,
      onboardingHandoffSession: null,
    });
  },
});
